import { apiGetCustomers } from "@lib/api/customer";
import {
  apiGetCustomerLeadAccounts,
  apiGetCustomerLeads,
} from "@lib/api/customerLead";
import { apiGetSendByteWalletAddress } from "@lib/api/walletSendByte";
import CustomerLeadName from "@lib/components/MiniComponents/Others/CustomerLeadName";
import CustomerName from "@lib/components/MiniComponents/Others/CustomerName";
import { StyledFlex } from "@lib/components/MiniComponents/Table/index.styled";
import { walletAddressTypeImage } from "@lib/helpers/ColorHelper";
import { useTypedSelector } from "@lib/redux";
import { AuthRoles } from "@lib/types";
import { UseMutationProps } from "@lib/types/Api";
import { Customer } from "@lib/types/Customer";
import { CustomerLead, CustomerLeadAccount } from "@lib/types/CustomerLead";
import { SendBytePaymentRequest } from "@lib/types/SendByte";
import { useQuery } from "@tanstack/react-query";
import { Button, Form, Image, Input, Modal, Select, Space } from "antd";
import { useCallback, useEffect } from "react";
import { FormattedMessage } from "react-intl";
import useIntl from "react-intl/lib/src/components/useIntl";

const layout = {
  labelCol: { span: 8 },
  wrapperCol: { span: 16 },
};

type Props<T> = {
  openModal: boolean;
  toggleModal: (status?: boolean) => void;
  mutationQuery: UseMutationProps<Partial<T>>;
  customer?: Customer;
  customerLead?: CustomerLead;
};

export default function SendBytePaymentRequestModal<
  T extends SendBytePaymentRequest,
>({ openModal, toggleModal, mutationQuery, customer, customerLead }: Props<T>) {
  const { messages } = useIntl();
  const { user } = useTypedSelector((state) => state.auth);
  const userRole = user?.role;
  const [form] = Form.useForm();
  const customerId =
    customerLead?.customerId ||
    customer?.id ||
    user?.customerId ||
    Form.useWatch("customerId", form);
  const customerUserId =
    customerLead?.id || Form.useWatch("customerUserId", form);
  const baseTpNumber = Form.useWatch("baseTpNumber", form);

  const { mutate, isPending } = mutationQuery;

  const initialValues = {
    customerId: customerLead?.customerId || customer?.id,
    customerUserId: customerLead?.id,
  };

  const handleResetForm = useCallback(() => {
    form?.resetFields();
  }, []);

  const handleClose = useCallback(() => {
    toggleModal(false);
    handleResetForm();
  }, [toggleModal, form]);

  useEffect(() => {
    if (openModal) {
      handleResetForm();
    }
  }, [openModal]);

  const { data: customers, isLoading: customersIsLoading } = useQuery({
    queryFn: () => apiGetCustomers({ limit: 100 }),
    queryKey: ["customers-dropdown"],
    enabled: openModal && userRole === AuthRoles.Admin,
  });

  const { data: customerLeads, isLoading: customerLeadsIsLoading } = useQuery({
    queryFn: () =>
      apiGetCustomerLeads({
        limit: 100,
        filter: [customerId ? `customerId||$eq||${customerId}` : ""],
      }),
    queryKey: ["customer-leads-dropdown", { customerId }],
    enabled: openModal && !!customerId,
  });

  const {
    data: customerLeadAccounts,
    isLoading: customerLeadAccountsIsLoading,
  } = useQuery({
    queryFn: () => apiGetCustomerLeadAccounts(customerUserId),
    queryKey: ["customer-lead-accounts-dropdown", { customerUserId }],
    enabled: openModal && !!customerUserId,
  });

  const { data: walletAddress, isLoading: walletAddressIsLoading } = useQuery({
    queryFn: () =>
      apiGetSendByteWalletAddress({
        limit: 100,
        filter: [
          `customerId||$eq||${customerId}`,
          `customerUserId||$eq||${customerUserId}`,
        ],
      }),
    queryKey: ["wallet-address-dropdown", { customerId, customerUserId }],
    enabled: openModal && !!customerId && !!customerUserId && !!baseTpNumber,
  });

  return (
    <Modal
      title={
        <FormattedMessage
          id={"common.createTitle"}
          values={{
            title: messages["sidebar.sendBytePaymentRequest"] as string,
          }}
        />
      }
      open={openModal}
      onCancel={handleClose}
      footer={null}
      confirmLoading={
        isPending
        // addMultipleQuery.isPending
      }
    >
      <Form<T>
        {...layout}
        form={form}
        name={"sidebar.sendBytePaymentRequest-modal"}
        onFinish={(values) => {
          const data: any = {
            // walletAddressId: values?.walletAddressId === "all" ? null : values?.walletAddressId,
            customerUserId: customerLead?.id || values?.customerUserId,
            baseTpNumber: values?.baseTpNumber,
            customerId: customer?.id || values?.customerId,
          };
          if (values?.sendByteWalletAddressId !== "all") {
            data.sendByteWalletAddressId = values?.sendByteWalletAddressId;
          }
          mutate(data as Partial<T>);
        }}
        style={{ maxWidth: 600, marginTop: 20 }}
        autoComplete="off"
        initialValues={initialValues}
      >
        {userRole === AuthRoles.Admin && (
          <Form.Item
            name="customerId"
            label={messages["common.customer"] as string}
            rules={[{ required: true }]}
          >
            {customer ? (
              <>
                <Input type="hidden" />
                <CustomerName customer={customer} />
              </>
            ) : (
              <Select
                showSearch
                loading={customersIsLoading}
                onChange={() => {
                  form.setFieldValue("sendByteWalletAddressId", "all");
                }}
                optionFilterProp="text"
                placeholder={messages["common.select"] as string}
                options={customers?.data?.data?.map((customer) => ({
                  label: <CustomerName customer={customer} />,
                  text: customer.name,
                  value: customer.id,
                }))}
              />
            )}
          </Form.Item>
        )}

        <Form.Item
          name="customerUserId"
          label={messages["common.customerLead"] as string}
          rules={[{ required: true }]}
        >
          <Select
            showSearch
            loading={customerLeadsIsLoading}
            optionFilterProp="text"
            options={customerLeads?.data?.data?.map((customerLead) => ({
              label: <CustomerLeadName customerLead={customerLead} />,
              text: `${customerLead?.firstName} ${customerLead?.lastName}`,
              value: customerLead.id,
            }))}
            disabled={!customerId}
            placeholder={
              <FormattedMessage
                id={"helper.pleaseSelectToX"}
                values={{
                  x: messages[
                    customerId ? "common.customer" : "common.customerLead"
                  ] as string,
                }}
              />
            }
          />
        </Form.Item>

        <Form.Item
          name="baseTpNumber"
          label={messages["common.baseTpNumber"] as string}
          rules={[{ required: true }]}
        >
          <Select
            showSearch
            loading={customerLeadAccountsIsLoading}
            optionFilterProp="text"
            options={customerLeadAccounts?.data?.map(
              (item: CustomerLeadAccount) => ({
                label: item?.tpNumber,
                text: item?.tpNumber,
                value: item?.tpNumber,
              }),
            )}
            disabled={!customerUserId}
            placeholder={
              <FormattedMessage
                id={"helper.pleaseSelectToX"}
                values={{
                  x: messages[
                    !customerUserId
                      ? "common.customerLead"
                      : "common.baseTpNumber"
                  ] as string,
                }}
              />
            }
          />
        </Form.Item>

        <Form.Item
          name="sendByteWalletAddressId"
          label={messages["common.walletAddress"] as string}
          rules={[{ required: true }]}
        >
          <Select
            showSearch
            loading={walletAddressIsLoading}
            optionFilterProp="text"
            options={[
              {
                label: messages["common.all"] as string,
                text: messages["common.all"] as string,
                value: "all",
              },

              ...(walletAddress?.data?.data?.map((item) => ({
                label: (
                  <>
                    <Image
                      src={walletAddressTypeImage(item?.network)}
                      style={{ width: "15px", height: "15px" }}
                    />{" "}
                    {item?.address}
                  </>
                ),
                text: item?.address,
                value: item?.id,
              })) || []),
            ]}
            disabled={!customerUserId}
            placeholder={
              <FormattedMessage
                id={"helper.pleaseSelectToX"}
                values={{ x: messages["common.walletAddress"] as string }}
              />
            }
          />
        </Form.Item>

        <StyledFlex style={{ justifyContent: "end" }}>
          <Space>
            <Button
              htmlType="button"
              onClick={handleClose}
              disabled={
                isPending
                // addMultipleQuery.isPending
              }
            >
              {messages["common.reset"] as string}
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={
                isPending
                // addMultipleQuery.isPending
              }
            >
              {messages["common.create"] as string}
            </Button>
          </Space>
        </StyledFlex>
      </Form>
    </Modal>
  );
}
